import ButtonBase, { type ButtonBaseProps } from '@mui/material/ButtonBase';

import { type NudgePulse } from '@/components/atoms';

import { TipGlyph } from './TipGlyph';

/**
 * The disc's diameter.
 *
 * 48 and not the theme's 44px touch-target floor: this is the only control on
 * the site that has to be noticed from the corner of the eye before it is
 * read, and four pixels of extra disc is the cheapest way to buy that. It is
 * also the height the phone's sticky action bar rows settle at, so the two
 * pieces of chrome at the bottom of a phone screen are built on the same
 * module.
 */
export const TIP_LAUNCHER_SIZE = 48;

/**
 * `ref` is taken from `ButtonBaseProps` and passed on by spreading rather than
 * declared here, because under `exactOptionalPropertyTypes` an own optional
 * `ref` widens to include `undefined` and MUI's prop type rejects that when it
 * is handed over explicitly. It is focused after a dismissal, to put the caret
 * where the card was; see `ScrollNudge`.
 */
type Props = Pick<ButtonBaseProps, 'ref'> & {
  /** Spoken name — the chrome verb plus the tip's own eyebrow. */
  label: string;
  /** Which cadence to ping on, or `false` once it has stopped asking. */
  pulse: NudgePulse | false;
  onClick: () => void;
};

/**
 * The tip's anchor: a brand-red disc in the corner, which the card drops onto
 * and collapses back into.
 *
 * ## What it is for
 *
 * Two jobs, and they arrive in that order.
 *
 * **It is the thing that gets noticed.** The card that this replaced opened
 * cold in the bottom-left corner as a frosted panel tinted to match whatever
 * was behind it — in the lowest-attention region of the page, in the one
 * colour guaranteed to agree with its surroundings. It was, by construction,
 * unnoticeable. A saturated disc that pings once lands in peripheral vision
 * instead, and `NUDGE_CARD_LEAD_IN_MS` later the card opens into a corner the
 * eye is already pointed at.
 *
 * **It is what makes dismissal cheap.** Closing the card leaves this behind,
 * quiet — no ping, no second opening — so the tip stays one click away for the
 * rest of the session instead of being gone. That is what lets the card be
 * assertive in the first place: a visitor who closes it has not lost anything,
 * so the close button is not a decision about information. It is also why
 * nothing in this feature ever writes to `localStorage`.
 *
 * ## Why a `ButtonBase` and not a `Fab`
 *
 * `Fab` brings MUI's own sizing, elevation and hover transition, all of which
 * would have to be overridden, and it brings the floating-action-button
 * semantics with it — one per screen, the screen's primary action. That is
 * `FloatingWhatsApp`'s role on this site and it is in the opposite corner.
 * This is a disclosure control that happens to be round.
 *
 * The focus ring is the global `:focus-visible` rule in `theme/components.ts`,
 * so there is none here: a 2px brand outline offset 2px, which on a brand-red
 * disc lands on the page background rather than on the disc, and so stays
 * visible on both.
 */
export function TipLauncher({ label, pulse, onClick, ...rest }: Props) {
  return (
    <ButtonBase
      {...rest}
      onClick={onClick}
      aria-label={label}
      sx={{
        borderRadius: 999,
        // The disc supplies the colour; the shadow is what lifts it off
        // whatever band it has been scrolled over. 6 rather than the card's
        // 12: a 48px object with a card's shadow under it reads as hovering.
        boxShadow: 6,
        // The disc is already the full control, so the press feedback has to
        // be on the disc itself rather than on a background it does not have.
        transition: (t) => t.transitions.create('transform', { duration: 150 }),
        '&:hover': { transform: 'scale(1.06)' },
        '&:active': { transform: 'scale(0.96)' },
        '@media (prefers-reduced-motion: reduce)': {
          '&:hover, &:active': { transform: 'none' },
        },
      }}
    >
      <TipGlyph size={TIP_LAUNCHER_SIZE} pulse={pulse} />
    </ButtonBase>
  );
}
